import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import Form from '@afosto/app-components-react/Form';
import TextField from '@afosto/app-components-react/TextField';
import Checkbox from '@afosto/app-components-react/Checkbox';
import validationSchema from './validations';
import translations from './translations';
import RoutesFormSection from './RoutesFormSection';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

const formatRoutes = routes =>
  routes.reduce(
    (acc, route) => [
      ...acc,
      {
        path: route?.path,
        uri: route?.origin?.uri,
        isBucket: route?.origin?.is_bucket,
        isAsOrigin: route?.settings?.cache?.is_as_origin,
        isPublic: route?.settings?.cache?.is_public,
        age: route?.settings?.cache?.age,
        isRequired: route?.settings?.auth?.is_required,
        scope: route?.settings?.auth?.scope,
      },
    ],
    [],
  );

const ProxyForm = ({ proxy, inline, onSubmit, onCancel }) => {
  const intl = useIntl();

  const form = useForm({
    validationSchema,
    editInline: inline,
    defaultValues: {
      key: proxy?.key ?? '',
      routes: (proxy?.routes && formatRoutes(proxy?.routes)) ?? null,
      authSignatureSecret: proxy?.auth?.signature?.secret ?? null,
      authSignatureAuthIsRequired: proxy?.auth?.signature?.auth?.is_required ?? false,
      authSignatureAuthScope: proxy?.auth?.signature?.auth?.scope ?? null,
      authSignatureCacheIsAsOrigin: proxy?.auth?.signature?.cache?.is_as_origin ?? false,
      authSignatureCacheIsPublic: proxy?.auth?.signature?.cache?.is_public ?? false,
      authSignatureCacheAge: proxy?.auth?.signature?.cache?.age ?? 31536000,
      oauthIssuer: proxy?.oauth?.issuer ?? null,
      oauthClientId: proxy?.oauth?.client_id ?? null,
      oauthAuthorizeUrl: proxy?.oauth?.authorizeUrl ?? null,
      oauthReturnUrlKey: proxy?.oauth?.returnUrlKey ?? null,
    },
    onCancel,
    onSubmit,
  });

  return (
    <Form form={form}>
      <Box mb={2}>
        <TextField
          name="key"
          label={intl.formatMessage(translations.fields.key.label)}
          readOnly={!!proxy?.key}
          inline={!!proxy?.key}
        />
      </Box>

      <Box mb={3}>
        <RoutesFormSection />
      </Box>

      <Box mb={3}>
        <Typography variant="overline" color="textPrimary">
          {intl.formatMessage(translations.sections.auth)}
        </Typography>

        <TextField
          name="authSignatureSecret"
          label={intl.formatMessage(translations.fields.secret.label)}
        />

        <Checkbox
          name="authSignatureAuthIsRequired"
          label={intl.formatMessage(translations.fields.isRequired.label)}
        />
        <TextField
          name="authSignatureAuthScope"
          label={intl.formatMessage(translations.fields.scope.label)}
        />

        <Checkbox
          name="authSignatureCacheIsAsOrigin"
          label={intl.formatMessage(translations.fields.isAsOrigin.label)}
        />
        <Checkbox
          name="authSignatureCacheIsPublic"
          label={intl.formatMessage(translations.fields.isPublic.label)}
        />
        <TextField
          name="authSignatureCacheAge"
          label={intl.formatMessage(translations.fields.age.label)}
        />
      </Box>

      <Typography variant="overline" color="textPrimary">
        {intl.formatMessage(translations.sections.oauth)}
      </Typography>

      <TextField name="oauthIssuer" label={intl.formatMessage(translations.fields.issuer.label)} />
      <TextField
        name="oauthClientId"
        label={intl.formatMessage(translations.fields.clientId.label)}
      />
      <TextField
        name="oauthAuthorizeUrl"
        label={intl.formatMessage(translations.fields.authorizeUrl.label)}
      />
      <TextField
        name="oauthReturnUrlKey"
        label={intl.formatMessage(translations.fields.returnUrlKey.label)}
      />
    </Form>
  );
};

ProxyForm.propTypes = {
  proxy: PropTypes.object,
  inline: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

ProxyForm.defaultProps = {
  proxy: {},
  inline: false,
  onSubmit: undefined,
  onCancel: undefined,
};

export default ProxyForm;
